import * as React from 'react';
import {compose} from 'redux';
import {RouteChildrenProps, withRouter} from 'react-router-dom';
import {reset} from 'redux-form';
import {useDataProvider} from '../../fetchApi/dataProvider';
import {FETCH} from '../../fetchApi/types';
import {
    endpointConnectToIntegration,
    endpointSubmitAdditionalConnectionData,
    integrationConnectionRedirectPathLocalStorageKey,
    integrationNameFreshBooks,
    resourceIntegrationCredentials,
    resourceIntegrations,
} from '../../constants';
import {NotificationManager} from 'react-notifications';
import {useTranslation} from 'react-i18next';
import {Paper, Typography, withStyles} from '@material-ui/core';
import {SimpleForm, SimpleFormOnlySaveToolbar} from '../../controlComponents';
import ReduxAutocompleteField from '../Fields/ReduxAutocompleteField';
import {useDispatch} from 'react-redux';
import * as _ from 'lodash';
import CallbackSpinner from '../Callback/CallbackSpinner';

const formName = 'fresh-books-organisation-choose-form';

interface InternalFreshBooksCallbackProps extends RouteChildrenProps {
    classes: Record<string, string>;
}

const CallbackView: React.FunctionComponent<InternalFreshBooksCallbackProps> = ({
    history,
    classes
}) => {
    const {dataProvider} = useDataProvider();
    const {t: translate} = useTranslation();
    const dispatch = useDispatch();

    const [data, setData] = React.useState({choices: [], state: null});
    const [loading, toggleLoading] = React.useState(false);

    React.useEffect(() => {
        dataProvider(FETCH, `${resourceIntegrationCredentials}/${endpointConnectToIntegration}`, {
            provider: integrationNameFreshBooks,
            callbackUrl: location.href
        })
            .then(data => {
                const answer = data.data;
                if (answer.choices) {
                    setData(answer);
                    return;
                }

                NotificationManager.success(
                    translate(
                        'integrations.notifications.connect',
                        {context: 'success'},
                    ),
                );

                let redirectPath = `/${resourceIntegrations}`;
                const localStorageRedirectPath = localStorage.getItem(integrationConnectionRedirectPathLocalStorageKey);
                if (localStorageRedirectPath) {
                    redirectPath = localStorageRedirectPath;
                    localStorage.removeItem(integrationConnectionRedirectPathLocalStorageKey);
                }

                history.push(redirectPath);
            })
            .catch((e) => {
                NotificationManager.error(
                    e.message,
                    translate(
                        'integrations.notifications.connect',
                        {context: 'fail'}
                    )
                );

                history.push(`/${resourceIntegrations}`);
            });

        return (): void => {
            dispatch(reset(formName));
        };
    }, []);

    const save = (values: Record<string, any>): void => {
        toggleLoading(true);
        dataProvider(FETCH, `${resourceIntegrationCredentials}/${endpointSubmitAdditionalConnectionData}`, {
            data: values,
            provider: integrationNameFreshBooks
        })
            .then(() => {
                NotificationManager.success(
                    translate(
                        'integrations.notifications.connect',
                        {context: 'success'},
                    ),
                );

                let redirectPath = `/${resourceIntegrations}`;
                const localStorageRedirectPath = localStorage.getItem(integrationConnectionRedirectPathLocalStorageKey);
                if (localStorageRedirectPath) {
                    redirectPath = localStorageRedirectPath;
                    localStorage.removeItem(integrationConnectionRedirectPathLocalStorageKey);
                }

                history.push(redirectPath);
            })
            .catch((e) => {
                NotificationManager.error(
                    e.message,
                    translate(
                        'integrations.notifications.connect',
                        {context: 'fail'},
                    ),
                );

                history.push(`/${resourceIntegrations}`);
            })
    };

    if (!data.choices.length) {
        return (
            <CallbackSpinner />
        );
    }

    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                <Typography className={classes.title}>
                    {translate('integrations.fresh_books.actions.choose_organisation')}
                </Typography>
                <SimpleForm
                    form={formName}
                    paperClassName={classes.form}
                    resource={resourceIntegrationCredentials}
                    save={save}
                    initialValues={{
                        state: data.state,
                        externalOrganisationId: _.get(data, 'choices[0].account_id', null)
                    }}
                    loading={loading}
                    toolbar={
                        <SimpleFormOnlySaveToolbar
                            toolbarClassName={classes.formToolbar}
                            SaveButtonProps={{
                                fullWidth: true,
                                noIcon: true,
                                saveButtonLabel: translate('common.actions.submit')
                            }}
                        />
                    }
                >
                    <ReduxAutocompleteField
                        label={translate('integrations.fresh_books.fields.business')}
                        name='externalOrganisationId'
                        choices={data.choices}
                        optionValue='account_id'
                        fullWidth
                    />
                </SimpleForm>
            </Paper>
        </div>
    );
};

// @ts-ignore
const FreshBooksCallback: React.ComponentType<object> = compose(
    withRouter,
    withStyles({
        paper: {
            width: 275,
            height: 200,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: 20,
        },
        root: {
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        form: {
            width: '100%'
        },
        formToolbar: {
            padding: 0
        },
        title: {
            fontSize: 22,
            fontWeight: 500
        }
    })
)(CallbackView);

export default FreshBooksCallback;